import type { JSX } from "react";

import { t } from "@impero-com/common/utils/i18n";
import { Space } from "antd";
import { Link } from "react-router-dom";

import { DeleteResourceLink } from "components/deleteResourceLink";

import { LegacyRoute } from "../../../legacyRoutes";
import { WebRoute } from "../../../routes";
import { useDeleteClassificationMutation } from "../queries";
import { Classification, ClassificationAndRights } from "../types";

interface ActionCellProps {
    classificationAndRights: ClassificationAndRights;
}

export function ActionCell({
    classificationAndRights,
}: ActionCellProps): JSX.Element {
    const { classification, rights } = classificationAndRights;

    return (
        <Space>
            {rights.canEdit && (
                <EditMappingLink classificationId={classification.id} />
            )}
            {rights.canEditAccessRights && (
                <EditAccessRightsLink classificationId={classification.id} />
            )}
            {rights.canDelete && (
                <DeleteClassificationLink classification={classification} />
            )}
        </Space>
    );
}

interface EditMappingLinkProps {
    classificationId: string;
}

function EditMappingLink({
    classificationId,
}: EditMappingLinkProps): JSX.Element {
    return (
        <a href={`${LegacyRoute.CLASSIFICATION_EDIT}/${classificationId}`}>
            {t("Classification_List_Action_EditMapping")}
        </a>
    );
}

interface EditAccessRightsLinkProps {
    classificationId: string;
}

function EditAccessRightsLink({
    classificationId,
}: EditAccessRightsLinkProps): JSX.Element {
    return (
        <Link
            to={`${WebRoute.REPORTS_CLASSIFICATION}/${classificationId}/access-rights`}
        >
            {t("Classification_List_Action_EditAccessRights")}
        </Link>
    );
}

interface DeleteClassificationLinkProps {
    classification: Classification;
}

function DeleteClassificationLink({
    classification,
}: DeleteClassificationLinkProps): JSX.Element {
    const { mutate, isPending } = useDeleteClassificationMutation();
    return (
        <DeleteResourceLink
            okButton={t("Classification_DeleteDialog_Confirm")}
            cancelButton={t("Classification_DeleteDialog_Cancel")}
            confirmationTitle={t("Classification_DeleteDialog_Title")}
            confirmationBody={t("Classification_DeleteDialog_Body", {
                classificationName: classification.title,
            })}
            deleteResource={() =>
                mutate({ classificationId: classification.id })
            }
            isPending={isPending}
        >
            {t("Classification_List_Action_Delete")}
        </DeleteResourceLink>
    );
}
